import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useSelector } from 'react-redux';

// Maximum length for tag names
const MAX_TAG_LENGTH = 50;

// Array of soft colors for tag backgrounds
const softColors = [
  '#E6F2FF', '#FFE6E6', '#E6FFE6', '#FFF2E6', '#E6E6FF',
  '#FFE6F2', '#F2FFE6', '#E6FFF2', '#FFE6FF', '#E6FFFF',
  '#F2E6FF', '#E6FFF9', '#FFF6E6', '#E6F9FF', '#FFE6F9',
  '#FFFDE7' // Yellow color
];

// Component for rendering the color picker icon
const ColorPickerIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-200 hover:scale-110">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" fill={color} stroke="#4A5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="7" cy="7" r="2" fill="#4A5568"/>
  </svg>
);

const TagSelector = ({ onTagSelect, selectedTag: propSelectedTag, existingTags, onTagSelected }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedTag, setSelectedTag] = useState(propSelectedTag);
  const [selectedColor, setSelectedColor] = useState(softColors[0]);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });

  const inputRef = useRef(null);
  const colorPickerRef = useRef(null);
  const colorButtonRef = useRef(null);
  const containerRef = useRef(null);

  const updateSuggestions = useCallback(() => {
    if (inputValue && !selectedTag && existingTags && existingTags.length > 0) {
      const filtered = existingTags.filter(tag => 
        tag.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [inputValue, selectedTag, existingTags]);

  useEffect(() => {
    updateSuggestions();
  }, [updateSuggestions]);

  useEffect(() => {
    if (propSelectedTag !== selectedTag) {
      setSelectedTag(propSelectedTag);
    }
  }, [propSelectedTag]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target) &&
          !colorButtonRef.current.contains(event.target)) {
        setIsColorPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (selectedTag) {
      setSelectedTag(null);
      onTagSelect(null);
    }
  };

  const handleTagSelect = (tag) => {
    setSelectedTag(tag);
    setInputValue('');
    onTagSelect(tag);
    setSuggestions([]);
    if (onTagSelected) {
      onTagSelected();
    }
  };

  const createNewTag = () => {
    const newTag = {
      id: `new-${Date.now()}`,
      name: inputValue.trim(),
      color: selectedColor,
    };
    handleTagSelect(newTag);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        handleTagSelect(suggestions[0]);
      } else if (inputValue.trim()) {
        createNewTag();
      }
    }
  };

  const removeTag = () => {
    setSelectedTag(null);
    setInputValue('');
    onTagSelect(null);
  };

  const toggleColorPicker = (e) => {
    // Add this line to prevent event propagation
    e.preventDefault();
    e.stopPropagation();
    
    if (!isColorPickerOpen) {
      const rect = colorButtonRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      
      let left = rect.left - containerRect.left;
      let top = rect.bottom - containerRect.top;

      if (left + 120 > containerRect.width) {
        left = containerRect.width - 120;
      }

      if (top + 120 > containerRect.height) {
        top = rect.top - containerRect.top - 120;
      }

      setColorPickerPosition({ top, left });
    }
    setIsColorPickerOpen(!isColorPickerOpen);
  };

  const handleColorSelect = (color, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedColor(color);
    setIsColorPickerOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center bg-white border-2 border-neutral-900 rounded-xl overflow-hidden focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-200">
        {selectedTag ? (
          <div className="flex items-center w-full px-3 py-2">
            <span 
              className="px-2 py-1 rounded-full text-sm"
              style={{ backgroundColor: selectedTag.color }}
            >
              {selectedTag.name}
            </span>
            <button 
              onClick={removeTag}
              className="ml-2 text-neutral-600 hover:text-neutral-800 focus:outline-none"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="flex-grow px-3 py-2 text-neutral-900 placeholder-neutral-400 focus:outline-none"
              placeholder="Type to search or create a tag"
              maxLength={MAX_TAG_LENGTH}
            />
            <button 
              ref={colorButtonRef}
              className="pr-3 focus:outline-none focus:ring-2 focus:ring-blue-200 flex items-center transition-all duration-200"
              onClick={toggleColorPicker}
            >
              <ColorPickerIcon color={selectedColor} />
            </button>
          </>
        )}
      </div>

      {isColorPickerOpen && (
        <div 
          ref={colorPickerRef}
          className="absolute bg-white border-2 border-neutral-900 rounded-xl shadow-lg z-50 p-2"
          style={{
            top: `${colorPickerPosition.top}px`,
            left: `${colorPickerPosition.left}px`,
          }}
        >
          <div className="grid grid-cols-4 gap-1">
            {softColors.map((color, index) => (
              <button
                key={`${color}-${index}`}
                onClick={(e) => handleColorSelect(color, e)}
                type="button"
                className={`w-6 h-6 rounded-full transition-transform duration-200 hover:scale-110 ${selectedColor === color ? 'ring-2 ring-blue-500' : ''}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {suggestions.length > 0 && !selectedTag && (
        <div className="absolute z-40 w-full bg-white border-2 border-neutral-900 mt-1 rounded-xl shadow-lg max-h-60 overflow-auto">
          {suggestions.map(tag => (
            <div
              key={tag.id}
              className="px-3 py-2 hover:bg-neutral-100 cursor-pointer"
              onClick={() => handleTagSelect(tag)}
            >
              <span 
                className="inline-block px-2 py-1 rounded-full text-sm"
                style={{ backgroundColor: tag.color }}
              >
                {tag.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {!selectedTag && inputValue && !suggestions.length && (
        <div className="absolute z-40 w-full bg-white border-2 border-neutral-900 mt-1 rounded-xl shadow-lg">
          <div
            className="px-3 py-2 hover:bg-neutral-100 cursor-pointer"
            onClick={createNewTag}
          >
            <span 
              className="inline-block px-2 py-1 rounded-full text-sm"
              style={{ backgroundColor: selectedColor }}
            >
              Create "{inputValue}" tag
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(TagSelector);
